import { useMemo } from 'react';
import tinycolor from 'tinycolor2';

import type { CustomStylesConfig } from '@webapp/common/components/custom-styles/custom-styles';
import { useMobile } from '@webapp/common/hooks/use-mobile';
import { ColorScheme, createMarginStyle, createTextStyle, getColor } from '@webapp/common/lib/ui';
import type { InfoDesignModel } from '@webapp/common/resources/entities/info';

export const useViewCustomStylesNew = ({
    brand: { backgroundColor },
    fonts: {
        buttonColor,
        buttonTextColor,
        buttonWidth,
        buttonHeight,
        buttonBorderRadius,
        elementColor,
        fieldBorderColor: fieldBorderColorOriginal,
        questionTextColor,
        replyTextBold,
        replyTextColor,
        replyTextItalic,
        replyTextSize,
        replyTextThrough,
        replyTextUnderline,
        titleBold,
        titleColor,
        titleItalic,
        titleSize,
        titleThrough,
        titleUnderline,
    },
    view: { gap, lineHeight, paddingBottom, paddingLeft, paddingRight, paddingTop, questionBlockPosition },
}: InfoDesignModel): CustomStylesConfig => {
    const parsedButtonColor = useMemo(() => {
        const color = typeof buttonColor === 'string' ? buttonColor.trim() : '';

        // buttonColor — либо обычный CSS-цвет ("#135b9dff", "rgb(...)"), либо JSON-массив
        // цветов градиента. Плаиновые строки в JSON не парсим: раньше каждый такой рендер
        // падал в catch с console.error и сорил в Sentry (FRONT-29KD и родственные).
        if (!color.startsWith('[') && !color.startsWith('"')) {
            return { backgroundColor: buttonColor };
        }

        try {
            const parsedBackgroundColor = JSON.parse(color);

            let backgroundColor: string;
            let background: string;

            if (Array.isArray(parsedBackgroundColor)) {
                if (parsedBackgroundColor.length === 1) {
                    backgroundColor = parsedBackgroundColor[0];
                } else if (parsedBackgroundColor.length > 1) {
                    background = `linear-gradient(90deg, ${parsedBackgroundColor[0]}, ${parsedBackgroundColor[1]}, ${parsedBackgroundColor[2]})`;
                    // сбрасываем background-color, чтобы не просвечивал цвет из css-класса
                    backgroundColor = 'transparent';
                }
            } else if (typeof parsedBackgroundColor === 'string') {
                backgroundColor = parsedBackgroundColor;
            }

            return { backgroundColor, background };
        } catch (e) {
            console.warn(e);
            return { backgroundColor: buttonColor };
        }
    }, [buttonColor]);

    const fieldBorderColorRgba = tinycolor(fieldBorderColorOriginal);
    const fieldBorderColor = fieldBorderColorRgba.toRgbString();
    const alfa10Color = fieldBorderColorRgba.setAlpha(0.1).toRgbString();
    const alfa30Color = fieldBorderColorRgba.setAlpha(0.3).toRgbString();
    const halfColor = fieldBorderColorRgba.setAlpha(0.5).toRgbString();
    const markColor = buttonTextColor === ColorScheme.LIGHT ? getColor('white') : getColor('black');
    const textStyle = createTextStyle({
        underline: replyTextUnderline,
        through: replyTextThrough,
        bold: replyTextBold,
        italic: replyTextItalic,
        color: replyTextColor,
        size: replyTextSize,
    });
    const isMobile = useMobile();

    return useMemo(
        () => ({
            fieldBorderColor,
            fieldBorderColor10: alfa10Color,
            fieldBorderColor30: alfa30Color,
            fieldBorderColor50: halfColor,
            elementColor,
            lineHeight,
            contentAlign: questionBlockPosition,
            gap,
            survey: createMarginStyle(paddingTop, paddingLeft, paddingBottom, paddingRight),
            datePicker: {
                root: {
                    '--borderColor': fieldBorderColor,
                } as any,
                arrow: {
                    color: fieldBorderColor,
                },
            },
            checkbox: {
                root: {
                    border: `1px solid ${fieldBorderColor}`,
                    backgroundColor: 'transparent',
                },
                checked: {
                    backgroundColor: fieldBorderColor,
                    color: markColor,
                },
                label: {
                    maxWidth: '80vw',
                    ...textStyle,
                },
            },
            question: {
                title: createTextStyle({
                    underline: titleUnderline,
                    through: titleThrough,
                    bold: titleBold,
                    italic: titleItalic,
                    color: titleColor,
                    size: titleSize,
                }),
                text: {
                    ...textStyle,
                    color: questionTextColor,
                },
            },
            text: textStyle,
            textField: {
                size: replyTextSize,
                backgroundColor: alfa10Color,
                color: replyTextColor,
                minWidth: 120,
                '--c-border-focus': fieldBorderColor,
                borderColor: alfa30Color,
            } as any,
            select: {
                arrow: {
                    color: fieldBorderColor,
                },
                dropdownBg: {
                    backgroundColor: halfColor,
                },
                activeItem: { backgroundColor: halfColor },
                item: {
                    backgroundColor: alfa10Color,
                    color: getColor('grey3'),
                },
                label: {
                    width: 'auto',
                    minWidth: '100%',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    color: replyTextColor,
                },
                root: {
                    backgroundColor: alfa10Color,
                    borderColor: alfa30Color,
                    '--c-border-focus': fieldBorderColor,
                    width: isMobile ? '100%' : 240,
                    maxWidth: isMobile ? undefined : 240,
                    ...textStyle,
                },
            },
            star: {
                root: {
                    stroke: alfa10Color,
                    fill: alfa10Color,
                },
            },
            icon: {
                root: {
                    fill: 'transparent',
                    stroke: fieldBorderColor,
                },
            },
            imageSlider: {
                root: {
                    color: elementColor,
                },
            },
            selectImage: {
                resizer: {
                    color: fieldBorderColor,
                },
            },
            button: {
                root: {
                    backgroundColor: parsedButtonColor.backgroundColor,
                    background: parsedButtonColor.background,
                    color: markColor,
                    width: buttonWidth,
                    height: buttonHeight,
                    borderRadius: buttonBorderRadius,
                },
            },
            matrix: {
                fixedHeader: {
                    backgroundColor: backgroundColor,
                },
            },
        }),
        [
            fieldBorderColor,
            lineHeight,
            questionBlockPosition,
            gap,
            paddingTop,
            paddingLeft,
            paddingBottom,
            paddingRight,
            markColor,
            textStyle,
            titleUnderline,
            titleThrough,
            titleBold,
            titleItalic,
            titleColor,
            titleSize,
            questionTextColor,
            replyTextSize,
            alfa10Color,
            replyTextColor,
            alfa30Color,
            halfColor,
            isMobile,
            elementColor,
            buttonColor,
            backgroundColor,
        ],
    );
};
